import MockAdapter from 'axios-mock-adapter';
import { MockRoute, MockRouteOptions } from './mockRoute';

export interface FeatureMock {
    register(adapter: MockAdapter, opts?: { delay?: boolean }): void;
    reset(): void;
}

/**
 * Base class for a feature's set of mocked API routes, meant to be used two ways:
 *
 * - Dev mode (`VITE_MOCK_API`): an `add<Feature>MockApi` shim instantiates the subclass
 *   once and calls `register(mockAdapter, { delay: true })`, so responses go through
 *   `delayedResponse`/`makeMockResponse` for simulated latency and the `[mocked]: ...`
 *   console logging that `mockUtils.ts` provides.
 * - Tests: a global test mock server instantiates the subclass once, calls
 *   `register(adapter, { delay: false })` for synchronous responses with no timers, and
 *   calls `reset()` between tests (after `adapter.reset()` wiped the adapter's handlers)
 *   before calling `register()` again to reattach them.
 *
 * Each declared route ends up as exactly one `adapter.on<Method>(url).reply(fn)` handler
 * (see `MockRoute`) rather than as extra ad-hoc `.replyOnce()` handlers, because
 * axios-mock-adapter@1.16.0 matches handlers in first-match-in-registration-order and a
 * permanent handler registered earlier would shadow any later `.replyOnce()` override for
 * the same url. Keeping per-test overrides inside the one stable handler sidesteps that
 * pitfall entirely.
 */
export abstract class BaseFeatureMock implements FeatureMock {
    private routes: MockRoute<any, any>[] = [];

    /** Declares a route, keeping track of it so `register`/`reset` can act on it later. */
    protected route<TRes, TBody = unknown>(options: MockRouteOptions<TRes>): MockRoute<TRes, TBody> {
        const route = new MockRoute<TRes, TBody>(options);
        this.routes.push(route);
        return route;
    }

    public register(adapter: MockAdapter, opts: { delay?: boolean } = {}): void {
        const delay = opts.delay ?? false;
        this.routes.forEach((route) => route.attach(adapter, { delay }));
    }

    /** Resets every route (once-queue, override, recorded calls), then calls `onReset()`. */
    public reset(): void {
        this.routes.forEach((route) => route.reset());
        this.onReset();
    }

    /** Subclass hook for extra reset work, e.g. `.resetCount()`-ing factories or rebuilding stateful backends. */
    protected onReset(): void {}
}
